import { MarketplaceType } from '@yoop/server-model/marketplace';
import type { TokenInfoResponse } from '@yoop/server-model/token-info';
import { OfferSubStatus } from '@yoop/server-model/token-info';
import type {
  EventDetailResponse,
  GetBidResponse,
  GetOffersUpgradabilityRequest,
  SimpleUserActivityEventResponse,
  UserActivityEventDetailResponse,
} from '@yoop/server-model/user-activity';
import { BidStatus, PriceDisplayMode } from '@yoop/server-model/user-activity';
import { dataMapFrom, isNilOrEmpty, isNotNilOrEmpty } from '@yoop/util';
import { datetime } from '@yoop/datetime';
import type {
  MyYoopEvent,
  MyYoopOffers,
  MyYoopRequestedEvent,
  UserProfile,
  MyPriceOffer,
  EventDetailData,
  EventTransactingTool,
} from '@yoop/whitelabel-data';
import { MyPriceOfferStatus } from '@yoop/whitelabel-data';
import { mediaFrom } from '@yoop/whitelabel-media';
import { hasPaidOffer } from '@yoop/whitelabel-receipt';
import isNil from 'ramda/src/isNil';
import { commonEventDataFrom } from './event-common.helper';
import {
  getMarketplaceTypePriority,
  isMarketplaceActive,
  transactingToolFrom,
} from './marketplace.helper';
import { requestedOffersInfoFrom } from './offer.helper';
import { detailTokensFrom, pendingTokensFrom, tokensFrom } from './token.helper';
import { receiptDataModelFrom } from './receipt.helper';

//duplicated from libs/util-token/src/lib/helper/get-tokens.ts
export const MIN_TOKENS_TO_SHOW_BULK_OPT = 8;

export const myYoopEventFrom = (
  response: SimpleUserActivityEventResponse,
  currentUser: UserProfile,
): MyYoopEvent => {
  const { tokens, extras } = tokensFrom({
    ownedTokens: response.tokenInformation,
    assignedTokens: response.assigneeTokenInformation,
    isResaleOpen: isMarketplaceActive(response.resaleMarketplace),
    currentUserProfile: currentUser,
  });

  return {
    ...commonEventDataFrom(response),
    nearestTransactingTool: transactingToolFrom(
      response.nextMarketplace,
      response.priceDisplay === PriceDisplayMode.ALL_IN_PRICE,
    ),
    extrasTokens: extras,
    pendingTokens: pendingTokensFrom(response.marketplaceInformation),
    eventAccessTokens: tokens,
  };
};

export const eventDetailDataFrom = (
  response: UserActivityEventDetailResponse,
  currentUser: UserProfile,
  upgradableOffers: Set<number>,
): EventDetailData => {
  const isResaleOpen = response.marketplaceInformation.some(
    (marketplace) =>
      marketplace.type === MarketplaceType.SECONDARY_EXCHANGE && isMarketplaceActive(marketplace),
  );
  const categoryDetailMap = dataMapFrom(response.categoryDetails ?? [], ({ id }) => id);
  const { tokens, extras } = detailTokensFrom({
    ownedTokens: response.tokenInformation,
    assignedTokens: response.assigneeTokenInformation,
    isResaleOpen,
    currentUserProfile: currentUser,
    upgradableOffers,
    accessControlSubMode: response.accessControlSubMode,
    //Duplicated logic from 'eventDoorsOpen'
    isDoorsOpen: datetime
      .now()
      .isBetween(response.doorsOpenDate, response.doorsCloseDate, undefined, '[)'),
    categoryDetailMap,
  });

  const receipts = response.receiptResponseList
    ?.filter(hasPaidOffer)
    .map((receipt) => receiptDataModelFrom(receipt, response.currency, categoryDetailMap));

  const numOfAssignableTokens =
    tokens.filter(({ canAssign }) => canAssign).length +
    extras.filter(({ canAssign }) => canAssign).length;
  const publicData = publicEventDataFrom(response);
  return {
    ...publicData,
    extrasTokens: extras,
    eventAccessTokens: tokens,
    canBulkTransfer: numOfAssignableTokens > MIN_TOKENS_TO_SHOW_BULK_OPT,
    myPriceOfferInfo: {
      ...publicData.myPriceOfferInfo,
      activeOffers: response.bids?.map(myPriceOfferFrom).filter(isNotNilOrEmpty) ?? [],
    },
    receipts,
  };
};

export const publicEventDataFrom = (
  response: UserActivityEventDetailResponse | EventDetailResponse,
): EventDetailData => {
  const sortedMarketplaces =
    response.marketplaceInformation?.sort((lhs, rhs) => {
      const canBuyYoop = isNil(lhs.minimumPrice);
      if (canBuyYoop === isNil(lhs.minimumPrice)) {
        return getMarketplaceTypePriority(lhs.type) - getMarketplaceTypePriority(rhs.type);
      }
      return canBuyYoop ? 1 : -1;
    }) ?? [];
  const transactingToolsMap: Record<string, EventTransactingTool> = {};
  const childrenTransactingTools: Record<string, EventTransactingTool[]> = {};

  sortedMarketplaces.forEach((marketplace) => {
    const _item = transactingToolFrom(
      marketplace,
      response.priceDisplay === PriceDisplayMode.ALL_IN_PRICE,
    );
    if (isNil(_item)) {
      return;
    }
    if (marketplace.parentMarketplaceId) {
      childrenTransactingTools[marketplace.parentMarketplaceId] =
        childrenTransactingTools[marketplace.parentMarketplaceId] ?? [];
      childrenTransactingTools[marketplace.parentMarketplaceId].push(_item);
    } else {
      transactingToolsMap[_item.id] = _item;
    }
  });
  for (const parentId in childrenTransactingTools) {
    if (transactingToolsMap[parentId]) {
      transactingToolsMap[parentId].childrenTransactingTools = childrenTransactingTools[parentId];
    }
  }

  const transactingTools = Object.values(transactingToolsMap);
  const activeMarketplace = transactingTools.find(({ startTime, endTime }) =>
    datetime.now().isBetween(startTime, endTime),
  );

  return {
    ...commonEventDataFrom(response),
    transactingTools,
    restrictions: response.eventRestrictions?.map((restriction) => ({
      id: restriction.id,
      description: restriction.description,
      icon: restriction.icon && mediaFrom(restriction.icon),
      url: restriction.url,
      secondaryDescription: restriction.secondaryDescription,
    })),
    canSeeInfo: !isNilOrEmpty(response.eventRestrictions),
    canBulkTransfer: false,
    canBuyMoreYoop: activeMarketplace?.canBuyYoop ?? false,
    canBuyMoreMerch: activeMarketplace?.canBuyMerchandise ?? false,
    canBuyMoreAddons: activeMarketplace?.canBuyAddons ?? false,
    canSeeReceipt: response.tokenInformation?.some(
      ({ offerResponse }) =>
        offerResponse?.offerSubStatus !== OfferSubStatus.REFUNDED &&
        (offerResponse?.amount ?? 0) > 0,
    ),
    myPriceOfferInfo: {
      enabled: response.exchangeInfo?.exchangeEnabled ?? false,
      availableCategories:
        response.exchangeInfo?.inventoryCategories.map(({ inventoryCategory, minBidPrice }) => ({
          id: inventoryCategory.id.toString(),
          color: inventoryCategory.displaySettings.color ?? '',
          name: inventoryCategory.name,
          detail: inventoryCategory.detail,
          minPrice: minBidPrice,
        })) ?? [],
      activeOffers: [],
    },
  };
};

export const myYoopRequestedEventFrom = (
  response: SimpleUserActivityEventResponse,
): MyYoopRequestedEvent => {
  let canBidMore = false;
  const bidWindow = response.exchangeInfo?.bidSubmissionWindow;
  if (response.exchangeInfo?.exchangeEnabled && bidWindow) {
    canBidMore = datetime
      .now()
      .isBetween(datetime.parse(bidWindow.startTime), datetime.parse(bidWindow.endTime));
  }
  let offers: MyYoopOffers[] = [];
  let canSubmitMoreOffers = false;
  response.marketplaceInformation?.map(requestedOffersInfoFrom).forEach((requestedInfo) => {
    offers = [...offers, ...requestedInfo.offers];
    canSubmitMoreOffers = canSubmitMoreOffers || requestedInfo.canSubmitMoreOffers;
  });
  return {
    ...commonEventDataFrom(response),
    nearestTransactingTool: transactingToolFrom(
      response.nextMarketplace,
      response.priceDisplay === PriceDisplayMode.ALL_IN_PRICE,
    ),
    requestedOffers: offers,
    myPriceOffers: response.bids?.map(myPriceOfferFrom).filter(isNotNilOrEmpty) ?? [],
    canSubmitAnotherBid: canBidMore,
    canSubmitAnotherOffer: canSubmitMoreOffers,
  };
};

const myPriceOfferFrom = (offer: GetBidResponse): MyPriceOffer | undefined => {
  const status = myPriceOfferStatusFrom(offer.status);
  if (isNil(status)) {
    // Filled/unknown bid statuses have no active client representation — skip.
    return undefined;
  }
  return {
    id: offer.offerId ?? 0,
    price: offer.price ?? 0,
    description: offer.inventoryCategoryName ?? '',
    yoopCount: offer.quantity ?? 0,
    categoryId: offer.inventoryCategoryId ?? 0,
    expireTime: offer.expireTime ?? '',
    status,
  };
};

const myPriceOfferStatusFrom = (status: BidStatus | undefined): MyPriceOfferStatus | undefined => {
  switch (status) {
    case BidStatus.OPEN:
      return MyPriceOfferStatus.Active;
    case BidStatus.EXPIRED:
      return MyPriceOfferStatus.Expired;
    case BidStatus.CANCELLED:
      return MyPriceOfferStatus.Cancelled;
    case BidStatus.FAILED:
      return MyPriceOfferStatus.Failed;
  }
  //BidStatus.FILLED
  return undefined;
};

export const upgradeOfferRequestFrom = (
  tokens: TokenInfoResponse[],
): GetOffersUpgradabilityRequest | undefined => {
  const upgradeGroup: Record<string, number[]> = {};
  tokens?.forEach(({ offerResponse, tokenId }) => {
    if (!isNilOrEmpty(offerResponse)) {
      upgradeGroup[offerResponse.id] = upgradeGroup[offerResponse.id] ?? [];
      upgradeGroup[offerResponse.id].push(parseInt(tokenId));
    }
  });

  if (isNilOrEmpty(upgradeGroup)) {
    return undefined;
  }

  return {
    offerUpgradeOptionsRequestItems: Object.entries(upgradeGroup).map(([key, value]) => ({
      sourceOfferId: parseInt(key),
      inventoryItemIdsForUpgrade: value,
    })),
  };
};
